<template>
  <div class="product-list">
    <div class="header">
      <h2>
        布料管理
        <el-tag v-if="syncHealth" size="small" :type="syncTagType" style="margin-left:8px">
          {{ syncLabel }}
        </el-tag>
      </h2>
      <div>
        <el-button type="primary" @click="handleAdd">添加布料</el-button>
      </div>
    </div>

    <!-- 搜索区域 -->
    <div class="search-section">
      <el-row :gutter="16">
        <el-col :span="4">
          <el-input
            v-model="searchFilters.brand_name"
            placeholder="按布料名称搜索"
            clearable
            @input="handleSearch"
          />
        </el-col>
        <el-col :span="4">
          <el-input
            v-model="searchFilters.title"
            placeholder="按产品标题搜索"
            clearable
            @input="handleSearch"
          />
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="searchFilters.year"
            placeholder="全部"
            @change="handleSearch"
          >
            <el-option label="全部" value="__all__" />
            <el-option
              v-for="year in availableYears"
              :key="year"
              :label="String(year)"
              :value="year"
            />
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="searchFilters.material"
            placeholder="全部"
            clearable
            filterable
            @change="handleSearch"
          >
            <el-option
              v-for="material in materials"
              :key="material"
              :label="material"
              :value="material"
            />
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="searchFilters.theme_series"
            placeholder="全部"
            clearable
            filterable
            @change="handleSearch"
          >
            <el-option
              v-for="series in themeSeries"
              :key="series"
              :label="series"
              :value="series"
            />
          </el-select>
        </el-col>
        <el-col :span="4">
          <el-select
            v-model="searchFilters.print_size"
            placeholder="全部"
            clearable
            filterable
            @change="handleSearch"
          >
            <el-option
              v-for="size in printSizes"
              :key="size"
              :label="size"
              :value="size"
            />
          </el-select>
        </el-col>
      </el-row>
    </div>

    <el-table 
      :data="filteredAndSortedProducts" 
      style="width: 100%" 
      v-loading="loading"
      :default-sort="{prop: 'year', order: 'descending'}"
      @sort-change="handleSortChange"
    >
              <el-table-column prop="id" label="ID" width="80" sortable show-overflow-tooltip />
      <el-table-column prop="brand_name" label="布料名称" min-width="120" sortable>
        <template #default="{ row }">
          <el-tooltip :content="(row && row.brand_name) || ''" placement="top" :disabled="!(row && row.brand_name)">
            <span>{{ (row && row.brand_name) || '-' }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="title" label="产品标题" min-width="150" sortable>
        <template #default="{ row }">
          <el-tooltip :content="(row && row.title) || ''" placement="top" :disabled="!(row && row.title)">
            <span>{{ (row && row.title) || '-' }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="year" label="发布年份" width="100" sortable>
        <template #default="{ row }">
          <span>{{ row && row.year != null && row.year !== '' ? row.year : '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="publish_month" label="发布月份" width="100" sortable>
        <template #default="{ row }">
          <span>{{ formatDisplayMonth(row && row.publish_month, row && row.year) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="material" label="材料" min-width="120" sortable>
        <template #default="{ row }">
          <el-tooltip :content="row.material" placement="top" :disabled="!row.material">
            <span>{{ row.material || '-' }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="theme_series" label="主题系列" min-width="120" sortable>
        <template #default="{ row }">
          <el-tooltip :content="row.theme_series" placement="top" :disabled="!row.theme_series">
            <span>{{ row.theme_series || '-' }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="print_size" label="印制尺寸" min-width="120" sortable>
        <template #default="{ row }">
          <el-tooltip :content="row.print_size" placement="top" :disabled="!row.print_size">
            <span>{{ row.print_size || '-' }}</span>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="创建时间" width="150" sortable>
        <template #default="{ row }">
          <span>{{ row.created_at || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="updated_at" label="更新时间" width="150" sortable>
        <template #default="{ row }">
          <span>{{ row.updated_at || '-' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220" fixed="right">
        <template #default="{ row }">
          <div class="operation-buttons">
            <el-button size="small" type="primary" @click="handleEdit(row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(row)">删除</el-button>
            <el-button size="small" type="success" @click="openImageManager(row)">图片管理</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :total="listTotal"
        :page-size="pageSize"
        :current-page="currentPage"
        :page-sizes="[30, 50, 100]"
        @size-change="onPageSizeChange"
        @current-change="onPageChange"
      />
    </div>

    <!-- 新增/编辑布料对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="60%"
      :close-on-click-modal="false"
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="100px"
        style="max-height: 60vh; overflow-y: auto;"
      >
        <el-form-item label="布料名称" prop="brand_name">
          <el-input v-model="form.brand_name" placeholder="请输入布料名称" />
        </el-form-item>
        <el-form-item label="产品标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入产品标题" />
        </el-form-item>
        <el-form-item label="发布年份" prop="year">
          <el-date-picker
            v-model="form.year"
            type="year"
            placeholder="选择年份"
            value-format="YYYY"
            format="YYYY"
          />
        </el-form-item>
        <el-form-item label="发布月份" prop="publish_month">
          <el-date-picker
            v-model="form.publish_month"
            type="month"
            placeholder="选择月份"
            value-format="YYYY-MM"
            format="YYYY-MM"
          />
        </el-form-item>
        <el-form-item label="材料" prop="material">
          <el-select
            v-model="form.material"
            placeholder="请选择材料"
            filterable
            allow-create
            default-first-option
            @create="handleCreateMaterial"
          >
            <el-option
              v-for="item in materials"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="主题系列" prop="theme_series">
          <el-select
            v-model="form.theme_series"
            placeholder="请选择主题系列"
            filterable
            allow-create
            default-first-option
            @create="handleCreateThemeSeries"
          >
            <el-option
              v-for="item in themeSeries"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="印制尺寸" prop="print_size">
          <el-select
            v-model="form.print_size"
            placeholder="请选择印制尺寸"
            filterable
            allow-create
            default-first-option
            @create="handleCreatePrintSize"
          >
            <el-option
              v-for="item in printSizes"
              :key="item"
              :label="item"
              :value="item"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="灵感来源" prop="inspiration_origin">
          <el-input
            v-model="form.inspiration_origin"
            type="textarea"
            :rows="9"
            placeholder="请输入灵感来源"
            resize="vertical"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 统一图片管理（展示已有图 + 上传 + 拖拽排序） -->
    <image-viewer
      v-if="imageManagerVisible && currentProduct"
      :visible="true"
      :product-id="currentProduct.id"
      :product-meta="currentProduct"
      :title="imageManagerTitle"
      :color="currentColor"
      @close="closeImageManager"
    />

    <!-- 删除确认对话框 -->
    <el-dialog
      title="确认删除"
      v-model="deleteDialogVisible"
      width="30%"
    >
      <p>确定要删除这条布料记录吗？此操作不可恢复。</p>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="deleteDialogVisible = false">取消</el-button>
          <el-button type="danger" @click="confirmDelete" :loading="deleting">
            确定删除
          </el-button>
        </span>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useStore } from 'vuex'
import axios from 'axios'
import ImageViewer from '../components/ImageViewer.vue'

export default {
  name: 'ProductList',
  components: {
    ImageViewer
  },
  setup() {
    const store = useStore()
    const loading = ref(false)
    const dialogVisible = ref(false)
    const imageManagerVisible = ref(false)
    const submitting = ref(false)
    const currentProduct = ref(null)
    const formRef = ref(null)
    const deleteDialogVisible = ref(false)
    const products = ref([])
    const currentColor = ref('')


    const dialogTitle = computed(() => {
      return currentProduct.value ? '编辑布料' : '添加布料'
    })

    const tableSort = reactive({ prop: 'year', order: 'descending' })

    const monthNum = (m, year) => {
      if (m == null || m === '') return 0
      if (typeof m === 'number') return m
      const s = String(m)
      if (s.includes('-')) {
        const parts = s.split('-')
        return parseInt(parts[1] || parts[0], 10) || 0
      }
      return parseInt(s, 10) || 0
    }

    const pageSize = ref(30)
    const currentPage = ref(1)
    const listTotal = ref(0)
    const serverPaging = ref(true)

    const filteredAndSortedProducts = computed(() => {
      // 服务端分页时直接展示当前页
      if (serverPaging.value) {
        return (products.value || []).filter((p) => p && typeof p === 'object')
      }

      let result = (products.value || []).filter((p) => p && typeof p === 'object')

      if (searchFilters.brand_name) {
        const q = searchFilters.brand_name.toLowerCase()
        result = result.filter(p => (p.brand_name || '').toLowerCase().includes(q))
      }
      if (searchFilters.title) {
        const q = searchFilters.title.toLowerCase()
        result = result.filter(p => (p.title || '').toLowerCase().includes(q))
      }
      if (searchFilters.year && searchFilters.year !== '__all__') {
        result = result.filter(p => String(p.year ?? '') === String(searchFilters.year))
      }
      if (searchFilters.material) {
        result = result.filter(p => p.material === searchFilters.material)
      }
      if (searchFilters.theme_series) {
        result = result.filter(p => p.theme_series === searchFilters.theme_series)
      }
      if (searchFilters.print_size) {
        result = result.filter(p => p.print_size === searchFilters.print_size)
      }

      const { prop, order } = tableSort
      if (prop && order) {
        const dir = order === 'ascending' ? 1 : -1
        result.sort((a, b) => {
          let av = a[prop]
          let bv = b[prop]
          if (prop === 'publish_month') {
            av = monthNum(av, a.year)
            bv = monthNum(bv, b.year)
          } else if (prop === 'year') {
            av = Number(av) || 0
            bv = Number(bv) || 0
          } else if (prop === 'created_at' || prop === 'updated_at') {
            av = av ? new Date(av).getTime() : 0
            bv = bv ? new Date(bv).getTime() : 0
          } else {
            av = av == null ? '' : av
            bv = bv == null ? '' : bv
            if (typeof av === 'string') return av.localeCompare(bv, 'zh') * dir
          }
          if (av === bv) {
            const ym = (Number(b.year) || 0) - (Number(a.year) || 0)
            if (ym) return ym
            return monthNum(b.publish_month, b.year) - monthNum(a.publish_month, a.year)
          }
          return av > bv ? dir : -dir
        })
      } else {
        result.sort((a, b) => {
          const ym = (Number(b.year) || 0) - (Number(a.year) || 0)
          if (ym) return ym
          return monthNum(b.publish_month, b.year) - monthNum(a.publish_month, a.year)
        })
      }
      listTotal.value = result.length
      const start = (currentPage.value - 1) * pageSize.value
      return result.slice(start, start + pageSize.value)
    })

    const handleSortChange = ({ prop, order }) => {
      tableSort.prop = prop
      tableSort.order = order
    }

    const searchFilters = reactive({
      brand_name: '',
      title: '',
      year: '__all__',
      material: '',
      theme_series: '',
      print_size: ''
    })

    const form = reactive({
      brand_name: '',
      title: '',
      year: '',
      publish_month: '',
      material: '',
      theme_series: '',
      print_size: '',
      inspiration_origin: ''
    })

    const normalizeProductList = (raw) => {
      if (Array.isArray(raw)) return raw.filter((p) => p && typeof p === 'object')
      if (raw && Array.isArray(raw.data)) return raw.data.filter((p) => p && typeof p === 'object')
      if (raw && Array.isArray(raw.items)) return raw.items.filter((p) => p && typeof p === 'object')
      if (raw && raw.data && Array.isArray(raw.data.items)) {
        return raw.data.items.filter((p) => p && typeof p === 'object')
      }
      return []
    }

    const buildListParams = () => {
      const params = {
        page: currentPage.value,
        limit: pageSize.value
      }
      if (searchFilters.brand_name) params.brand_name = searchFilters.brand_name
      if (searchFilters.title) params.title = searchFilters.title
      if (searchFilters.year && searchFilters.year !== '__all__') params.year = searchFilters.year
      if (searchFilters.material) params.material = searchFilters.material
      if (searchFilters.theme_series) params.theme_series = searchFilters.theme_series
      if (searchFilters.print_size) params.print_size = searchFilters.print_size
      return params
    }

    const fetchProducts = async () => {
      if (!store.state.isAuthenticated) {
        return
      }
      loading.value = true
      try {
        const { data } = await axios.get('/api/products/', { params: buildListParams() })
        // 新分页形状 { code, data: { items, total, page, limit } }
        if (data && data.data && Array.isArray(data.data.items)) {
          serverPaging.value = true
          products.value = normalizeProductList(data.data.items)
          listTotal.value = data.data.total || 0
          currentPage.value = data.data.page || currentPage.value
          pageSize.value = data.data.limit || pageSize.value
          applyFacets(data.data.facets)
        } else if (Array.isArray(data)) {
          // 兼容旧全量数组：前端分页
          serverPaging.value = false
          products.value = normalizeProductList(data)
          listTotal.value = products.value.length
        } else {
          serverPaging.value = true
          products.value = normalizeProductList(data)
          listTotal.value = products.value.length
        }
        updateFilterOptions()
      } catch (error) {
        console.error('获取布料列表失败:', error)
        try {
          await store.dispatch('fetchProducts')
          serverPaging.value = false
          products.value = normalizeProductList(store.state.products)
          listTotal.value = products.value.length
          updateFilterOptions()
        } catch (e2) {
          products.value = []
          listTotal.value = 0
          ElMessage.error('获取布料列表失败: ' + (error.response?.data?.message || error.message))
        }
      } finally {
        loading.value = false
      }
    }

    const onPageChange = (p) => {
      currentPage.value = p
      if (serverPaging.value) fetchProducts()
    }

    const onPageSizeChange = (s) => {
      pageSize.value = s
      currentPage.value = 1
      if (serverPaging.value) fetchProducts()
    }

    const availableYears = ref([])

    const applyFacets = (facets) => {
      if (!facets || typeof facets !== 'object') return
      if (Array.isArray(facets.years)) {
        availableYears.value = facets.years.slice()
      }
      if (Array.isArray(facets.materials)) {
        materials.value = [...new Set([...materials.value, ...facets.materials])]
      }
      if (Array.isArray(facets.theme_series)) {
        themeSeries.value = [...new Set([...themeSeries.value, ...facets.theme_series])]
      }
      if (Array.isArray(facets.print_sizes)) {
        printSizes.value = [...new Set([...printSizes.value, ...facets.print_sizes])]
      }
    }

    const updateFilterOptions = () => {
      const existingYears = [...new Set(products.value.map(p => p.year).filter(Boolean))]
      availableYears.value = [...new Set([...availableYears.value, ...existingYears])]
        .sort((a, b) => Number(b) - Number(a))
      const existingMaterials = [...new Set(products.value.map(p => p.material).filter(Boolean))]
      materials.value = [...new Set([...materials.value, ...existingMaterials])]
      const existingThemeSeries = [...new Set(products.value.map(p => p.theme_series).filter(Boolean))]
      themeSeries.value = [...new Set([...themeSeries.value, ...existingThemeSeries])]
      const existingPrintSizes = [...new Set(products.value.map(p => p.print_size).filter(Boolean))]
      printSizes.value = [...new Set([...printSizes.value, ...existingPrintSizes])]
    }

    const handleSearch = () => {
      currentPage.value = 1
      if (serverPaging.value) {
        fetchProducts()
      }
    }

    // 表单验证规则
    const rules = {
      brand_name: [
        { required: true, message: '请输入布料名称', trigger: 'blur' }
      ],
      title: [
        { required: true, message: '请输入产品标题', trigger: 'blur' }
      ],
      year: [
        { required: true, message: '请选择发布年份', trigger: 'change' }
      ],
      publish_month: [
        { required: true, message: '请选择发布月份', trigger: 'change' }
      ]
    }

    // 材料、主题系列、印制尺寸选项
    const materials = ref([
      '雪纺印花', '150D斜长丝', '厚弘纹麻', '旗袍定位花', '循环印花材'
    ])
    const themeSeries = ref([
      '面韵春秋系列', '长物志系列', '循环印花系列'
    ])
    const printSizes = ref([
      '循环印花料', '旗袍定位料', '单边定位裙料'
    ])

    // 重置表单
    const resetForm = () => {
      Object.assign(form, {
        brand_name: '',
        title: '',
        year: '',
        publish_month: '',
        material: '',
        theme_series: '',
        print_size: '',
        inspiration_origin: ''
      })
    }

    const handleAdd = () => {
      currentProduct.value = null
      resetForm()
      dialogVisible.value = true
    }

    const handleEdit = (row) => {
      currentProduct.value = row
      // 填充表单数据，确保年份格式正确
      Object.assign(form, {
        brand_name: row.brand_name || '',
        title: row.title || '',
        year: row.year ? String(row.year) : '', // 确保年份为字符串格式
        publish_month: row.publish_month || '',
        material: row.material || '',
        theme_series: row.theme_series || '',
        print_size: row.print_size || '',
        inspiration_origin: row.inspiration_origin || ''
      })
      dialogVisible.value = true
    }

    // 提交表单
    const handleSubmit = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        submitting.value = true

        const url = currentProduct.value 
          ? `/api/products/${currentProduct.value.id}` 
          : '/api/products/'
        const method = currentProduct.value ? 'put' : 'post'

        await axios[method](url, form)
        
        ElMessage.success(currentProduct.value ? '更新成功' : '添加成功')
        dialogVisible.value = false
        await fetchProducts()
      } catch (error) {
        if (error.response) {
          ElMessage.error(error.response.data.message || '操作失败')
        } else {
          ElMessage.error('操作失败')
        }
        console.error('提交失败:', error)
      } finally {
        submitting.value = false
      }
    }

    // 创建新材料
    const handleCreateMaterial = (value) => {
      if (value && !materials.value.includes(value)) {
        materials.value.push(value)
      }
    }

    // 创建新主题系列
    const handleCreateThemeSeries = (value) => {
      if (value && !themeSeries.value.includes(value)) {
        themeSeries.value.push(value)
      }
    }

    // 创建新印制尺寸
    const handleCreatePrintSize = (value) => {
      if (value && !printSizes.value.includes(value)) {
        printSizes.value.push(value)
      }
    }

    const openImageManager = (row, color = '') => {
      currentProduct.value = row
      currentColor.value = color || ''
      imageManagerVisible.value = true
    }

    const closeImageManager = () => {
      imageManagerVisible.value = false
      currentColor.value = ''
      fetchProducts()
    }

    const imageManagerTitle = computed(() => {
      return currentProduct.value
        ? `${currentProduct.value.brand_name || ''} - 图片管理`
        : '图片管理'
    })

    // 格式化显示月份的函数
    const formatDisplayMonth = (publishMonth, year) => {
      if (!publishMonth) return '-'
      
      // 如果 publishMonth 是 "YYYY-MM" 格式，提取月份部分
      if (typeof publishMonth === 'string' && publishMonth.includes('-')) {
        const parts = publishMonth.split('-')
        if (parts.length >= 2) {
          const month = parseInt(parts[1])
          return month ? `${month}月` : '-'
        }
      }
      
      // 如果是数字格式的月份
      if (typeof publishMonth === 'number') {
        return `${publishMonth}月`
      }
      
      return publishMonth || '-'
    }

    const syncHealth = ref(null)
    const syncLabel = computed(() => {
      if (!syncHealth.value) return '同步未知'
      const p = syncHealth.value.pending_products || 0
      const i = syncHealth.value.pending_images || 0
      if (syncHealth.value.success === false) return '同步失败'
      if (p + i > 0) return `待同步 ${p + i}`
      return '已同步'
    })
    const syncTagType = computed(() => {
      if (!syncHealth.value) return 'info'
      if (syncHealth.value.success === false) return 'danger'
      if ((syncHealth.value.pending_products || 0) + (syncHealth.value.pending_images || 0) > 0) return 'warning'
      return 'success'
    })
    const fetchSyncHealth = async () => {
      try {
        const { data } = await axios.get('/api/products/sync-health')
        syncHealth.value = data.data || null
      } catch (e) {
        syncHealth.value = null
      }
    }

    const deleting = ref(false)

    const confirmDelete = async () => {
      if (!currentProduct.value?.id) return
      deleting.value = true
      try {
        await axios.delete(`/api/products/${currentProduct.value.id}`)
        ElMessage.success('删除成功')
        deleteDialogVisible.value = false
        currentProduct.value = null
        await fetchProducts()
      } catch (error) {
        ElMessage.error(error.response?.data?.message || '删除失败')
        console.error('删除失败:', error)
      } finally {
        deleting.value = false
      }
    }

    const handleDelete = async (row) => {
      if (!row?.id) return
      try {
        await ElMessageBox.confirm(
          `确定要删除布料「${row.brand_name || row.title || row.id}」吗？此操作不可恢复。`,
          '提示',
          { type: 'warning', confirmButtonText: '删除', cancelButtonText: '取消' }
        )
        await axios.delete(`/api/products/${row.id}`)
        ElMessage.success('删除成功')
        await fetchProducts()
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          ElMessage.error(error.response?.data?.message || '删除失败')
          console.error('删除失败:', error)
        }
      }
    }

    /** 兼容旧模板/构建缓存中的 handleBatchDelete 引用 */
    const handleBatchDelete = handleDelete

    // 初始化
    onMounted(() => {
      fetchProducts()
      fetchSyncHealth()
    })

    return {
      loading,
      syncHealth,
      syncLabel,
      syncTagType,
      dialogVisible,
      imageManagerVisible,
      imageManagerTitle,
      submitting,
      currentProduct,
      formRef,
      searchFilters,
      form,
      rules,
      materials,
      themeSeries,
      printSizes,
      availableYears,
      fetchProducts,
      handleAdd,
      handleEdit,
      handleSubmit,
      handleDelete,
      handleBatchDelete,
      openImageManager,
      closeImageManager,
      handleCreateMaterial,
      handleCreateThemeSeries,
      handleCreatePrintSize,
      handleSearch,
      deleting,
      confirmDelete,
      deleteDialogVisible,
      dialogTitle,
      filteredAndSortedProducts,
      handleSortChange,
      currentColor,
      formatDisplayMonth,
      pageSize,
      currentPage,
      listTotal,
      onPageChange,
      onPageSizeChange
    }
  }
}
</script>

<style scoped>
.product-list {
  padding: 20px;
}
.pager {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
.search-section :deep(.el-select__selected-item),
.search-section :deep(.el-select__placeholder),
.search-section :deep(.el-input__inner) {
  color: #303133 !important;
  -webkit-text-fill-color: #303133;
  opacity: 1;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
}

.search-section {
  margin-bottom: 20px;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.search-section .el-row {
  margin: 0;
}

.search-section .el-col {
  padding: 0 8px;
}

.search-section .el-input,
.search-section .el-select {
  width: 100%;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.image-preview {
  margin-top: 20px;
}

.image-item {
  position: relative;
  margin-bottom: 10px;
}

.image-item .el-image {
  width: 100%;
  height: 150px;
  border-radius: 4px;
}

.image-actions {
  position: absolute;
  top: 5px;
  right: 5px;
  display: flex;
  gap: 5px;
}

:deep(.el-upload-dragger) {
  width: 100%;
}

:deep(.el-form-item__content) {
  flex-wrap: nowrap;
}

:deep(.el-date-editor.el-input) {
  width: 100%;
}

.operation-buttons {
  display: flex;
  gap: 8px;
}

/* 图片网格容器 */
.image-grid-container {
  width: 100%;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  padding: 16px;
}

.image-item {
  position: relative;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
}

.image-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-order-badge {
  position: absolute;
  bottom: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.drag-handle {
  position: absolute;
  bottom: 8px;
  right: 8px;
  cursor: move;
  color: #606266;
  font-size: 16px;
  padding: 4px;
}

.image-item:hover .image-actions {
  display: flex;
  gap: 8px;
}

/* 拖拽样式 */
.sortable-ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.sortable-chosen {
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
}

.sortable-drag {
  opacity: 0.8;
}

/* 待上传图片预览样式 */
.pending-preview {
  margin-top: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e1e8ed;
}

.pending-preview h4 {
  margin: 0 0 15px 0;
  color: #409eff;
  font-size: 14px;
}

.pending-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.pending-item {
  position: relative;
}

.pending-card {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
}

.pending-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.pending-image {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
}

.pending-overlay {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 20px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 50%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.pending-card:hover .pending-overlay {
  opacity: 1;
}

.pending-actions {
  position: absolute;
  top: 5px;
  right: 5px;
}

.pending-order {
  position: absolute;
  top: 5px;
  left: 5px;
  background: rgba(64, 158, 255, 0.9);
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.pending-name {
  padding: 5px;
  font-size: 12px;
  color: #606266;
  text-align: center;
  background: white;
  word-break: break-all;
  line-height: 1.2;
  height: 20px;
  overflow: hidden;
}

/* 已上传图片预览样式 */
.uploaded-preview {
  margin-top: 20px;
  padding: 15px;
  background-color: #f0f9ff;
  border-radius: 6px;
  border: 1px solid #bfdbfe;
}

.uploaded-preview h4 {
  margin: 0 0 15px 0;
  color: #059669;
  font-size: 14px;
}

.uploaded-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.uploaded-item {
  position: relative;
  cursor: move;
}

.uploaded-card {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s ease;
}

.uploaded-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.uploaded-image {
  width: 100%;
  height: 80px;
  object-fit: cover;
  display: block;
}

.uploaded-overlay {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 20px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, transparent 50%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.uploaded-card:hover .uploaded-overlay {
  opacity: 1;
}

.uploaded-actions {
  position: absolute;
  top: 5px;
  right: 5px;
}

.uploaded-order {
  position: absolute;
  top: 5px;
  left: 5px;
  background: rgba(16, 185, 129, 0.9);
  color: white;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

.uploaded-name {
  padding: 5px;
  font-size: 12px;
  color: #606266;
  text-align: center;
  background: white;
  word-break: break-all;
  line-height: 1.2;
  height: 20px;
  overflow: hidden;
}

.operation-buttons .el-button {
  margin: 0;  /* 覆盖 Element Plus 的默认边距 */
  flex: 1;    /* 让所有按钮宽度相等 */
  min-width: 60px; /* 设置最小宽度 */
}

.upload-area {
  width: 100%;
  margin-bottom: 20px;
}

/* 文件夹上传样式 */
.upload-methods {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.folder-upload {
  text-align: center;
  padding: 20px;
  border: 2px dashed #dcdfe6;
  border-radius: 6px;
  background-color: #fafafa;
  transition: all 0.3s ease;
}

.folder-upload:hover {
  border-color: #409eff;
  background-color: #f0f9ff;
}

.folder-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.preview-list {
  margin-top: 20px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 15px;
  margin-top: 15px;
}

.preview-item {
  position: relative;
  cursor: move;
}

.image-card {
  position: relative;
  overflow: hidden;
  transition: transform 0.2s ease;
}

.image-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.preview-image {
  width: 100%;
  height: 120px;
  object-fit: cover;
  display: block;
  border-radius: 4px;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s ease;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 5px;
}

.image-card:hover .image-overlay {
  opacity: 1;
}

.image-actions {
  display: flex;
  gap: 5px;
}

.image-order {
  background: rgba(255, 255, 255, 0.9);
  color: #333;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  min-width: 20px;
  text-align: center;
}

/* 拖拽排序样式 */
.sortable-ghost {
  opacity: 0.5;
}

.sortable-chosen {
  transform: scale(1.05);
}

.sortable-drag {
  transform: rotate(5deg);
}

/* 产品信息头部样式 */
.product-info-header {
  margin-bottom: 15px;
}

.product-info-header .el-alert {
  border-radius: 6px;
}

/* 空状态样式 */
.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-type-state {
  text-align: center;
  padding: 20px;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>